import { platformById } from "@ondit/publishing";
import { Button, EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useAccess } from "~/core/access/index";
import { ApiError, api } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";
export function useExtensionReceipts(entity: string) {
	const { editor } = useAccess();
	const { locale } = useI18n();
	const queryClient = useQueryClient();
	useEffect(() => {
		if (!editor) return;
		const inflight = new Set<string>();
		const listener = async (event: MessageEvent) => {
			const m = event.data;
			if (
				event.source !== window ||
				event.origin !== location.origin ||
				m?.source !== "ondit-extension" ||
				m.type !== "receipt" ||
				m.entity_id !== entity ||
				typeof m.task_id !== "string" ||
				typeof m.receipt_id !== "string" ||
				!["filled", "user_reported", "failed"].includes(m.state) ||
				inflight.has(m.receipt_id)
			)
				return;
			inflight.add(m.receipt_id);
			try {
				await api.reportPlatformTask(entity, m.task_id, m.state, m.published_url);
				await queryClient.invalidateQueries({ queryKey: [...keys.entity(entity), "platform-tasks"] });
				window.postMessage({ source: "ondit-panel", type: "receipt_ack", receipt_id: m.receipt_id }, location.origin);
			} catch (e) {
				toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
			} finally {
				inflight.delete(m.receipt_id);
			}
		};
		window.addEventListener("message", listener);
		const sync = () =>
			window.postMessage({ source: "ondit-panel", type: "receipts", entity_id: entity }, location.origin);
		sync();
		const timer = setInterval(sync, 5000);
		window.addEventListener("focus", sync);
		return () => {
			clearInterval(timer);
			window.removeEventListener("focus", sync);
			window.removeEventListener("message", listener);
		};
	}, [entity, editor, locale, queryClient]);
}
export function PlatformTaskHistory({ entityId }: { entityId: string }) {
	const t = useT();
	const { editor } = useAccess();
	const { locale } = useI18n();
	const queryClient = useQueryClient();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "platform-tasks"],
		queryFn: () => api.platformTasks(entityId),
		...read,
		// The extension and WordPress report back on their own clock.
		refetchInterval: 10_000,
	});
	const cancel = useMutation({
		mutationFn: (id: string) => api.reportPlatformTask(entityId, id, "cancelled"),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	return (
		<section className="stack">
			<h3>{t("publishing.platform.history")}</h3>
			<Async state={state}>
				{(data) =>
					data.tasks.length ? (
						<div className="table-scroll">
							<Table variant="resource">
								<TableHeader>
									<TableRow>
										<TableHead>{t("distribution.article")}</TableHead>
										<TableHead>{t("distribution.platform")}</TableHead>
										<TableHead>{t("distribution.status")}</TableHead>
										<TableHead>
											<span className="sr-only">{t("common.cancel")}</span>
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.tasks.map((task) => (
										<TableRow key={task.id}>
											<TableCell>
												<Link
													to={`?tab=prepare&page=${encodeURIComponent(task.payload.source.page_id)}&platform=${encodeURIComponent(task.payload.platform)}&task=${encodeURIComponent(task.id)}`}
													reloadDocument
												>
													{task.payload.fields.title ||
														task.payload.fields.body?.slice(0, 80) ||
														platformById(task.payload.platform)?.name}
												</Link>
												<p className="hint">{platformById(task.payload.platform)?.name}</p>
											</TableCell>
											<TableCell>{platformById(task.payload.platform)?.name}</TableCell>
											<TableCell>
												<span>{t(`publishing.platform.state.${task.state}`)}</span>
												{task.published_url && (
													<p>
														<a href={task.published_url} target="_blank" rel="noreferrer">
															{t("publishing.platform.result")}
														</a>
													</p>
												)}
											</TableCell>
											<TableCell>
												{["ready", "filled"].includes(task.state) && (
													<Button variant="default" size="sm" asChild>
														<Link
															reloadDocument
															to={`?tab=prepare&page=${encodeURIComponent(task.payload.source.page_id)}&platform=${encodeURIComponent(task.payload.platform)}&task=${encodeURIComponent(task.id)}`}
														>
															{t("publishFlow.resume")}
														</Link>
													</Button>
												)}
												{["ready", "filled", "failed"].includes(task.state) && (
													<Button
														variant="ghost"
														size="sm"
														disabled={!editor}
														onClick={async () => {
															try {
																await cancel.mutateAsync(task.id);
															} catch (e) {
																toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
															}
														}}
													>
														{t("common.cancel")}
													</Button>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					) : (
						<EmptyState size="compact" title={t("publishing.platform.none")} />
					)
				}
			</Async>
			{(state.data?.legacy?.length ?? 0) > 0 && (
				<section className="stack">
					<h3>{t("publishing.legacy.title")}</h3>
					<p>{t("publishing.legacy.help")}</p>
					<Table variant="resource">
						<TableHeader>
							<TableRow>
								<TableHead>{t("distribution.article")}</TableHead>
								<TableHead>{t("distribution.platform")}</TableHead>
								<TableHead>{t("distribution.status")}</TableHead>
								<TableHead>{t("publishing.legacy.remote")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{state.data?.legacy.map((item) => (
								<TableRow key={item.id}>
									<TableCell>{item.title}</TableCell>
									<TableCell>{item.channel}</TableCell>
									<TableCell>
										{t(`publishing.state.${item.state}`)}
										{item.published_url && (
											<p>
												<a href={item.published_url} target="_blank" rel="noreferrer">
													{t("publishing.platform.result")}
												</a>
											</p>
										)}
									</TableCell>
									<TableCell>{item.remote_id ?? "—"}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</section>
			)}
		</section>
	);
}
